import { Box, Divider, Flex, HStack, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import { formatPrice } from '@getjust/commons';
import { FeatureFlag } from '@getjust/constants';
import { AnimatePresence, motion, useDragControls } from 'framer-motion';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import {
  type CSSProperties,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cartDetailsDrawerOpenAtom } from '$business/atoms/cart-details-drawer';
import { useCart, useShop } from '$hooks/queries';
import CartDetail from '$src/components/CartDetail';
import { useThemeContext } from '$src/context/ThemeContext';
import { useLockBackgroundScroll } from '$src/hooks/client/useLockBackgroundScroll';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useAddDiscount } from '$src/hooks/mutations';
import { useUpdateLineItems } from '$src/hooks/mutations/useUpdateLineItems';
import { useEstimatedShippingAtom } from '$src/hooks/state/useEstimatedShippingAtom';
import { getThemeColors } from '$src/utils/themeColors';

import { FieldDiff } from './FieldDiff';
import { useCartFieldDiff } from './useCartFieldDiff';

const Chevron = ({ color, isOpen }: { color: string; isOpen: boolean }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    style={{
      transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 350ms cubic-bezier(0.32, 0.72, 0, 1)',
    }}
  >
    <path
      d="M6 9l6 6 6-6"
      stroke={color}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const MinusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const TagIcon = ({ size = 20 }: { size?: number } = {}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path
      d="M3 12V5a2 2 0 0 1 2-2h7l9 9-9 9-9-9z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="8" cy="8" r="1.5" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IOS_CURVE = [0.32, 0.72, 0, 1] as const;
// Sub-RAIL-perceptual-instant threshold: anything resolving under 80 ms feels
// instant, so we skip the loading silhouette entirely on cache hits. Above
// this, the silhouette has earned the right to appear (otherwise it would
// flash for a few frames and read as a glitch).
const SKELETON_DELAY_MS = 80;
// Locked header height for the collapsed cart-summary card. Sized to the
// 2-item case (3.75rem thumbnail stack + 0.75rem vertical padding × 2) so
// that the skeleton, the 1-item collapsed real, and the 2-item collapsed
// real all measure identically. Applied to both the skeleton silhouette and
// the real Flex header — without it, the AnimatePresence swap dips through
// the outer Box's minH for a frame and reads as a hitch ("saccadé").
const HEADER_HEIGHT = '84px';

// Mirror MobileFieldsLayout's naked-input pattern so the discount input has the
// same "no chrome" look as the rest of the redesign. See globals.scss for the
// `.mobile-naked-input` reset.
const NAKED_INPUT_CLASS = 'mobile-naked-input';
const NAKED_INPUT_STYLE: CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  display: 'block',
  flex: '1 1 auto',
  width: '100%',
  minWidth: 0,
  height: 'auto',
  margin: 0,
  padding: 0,
  border: 0,
  outline: 0,
  boxShadow: 'none',
  background: 'transparent',
  backgroundColor: 'transparent',
  borderRadius: 0,
  font: 'inherit',
  fontSize: '1rem',
  lineHeight: 1.4,
  color: 'var(--text, #1c1c1c)',
};

/**
 * Imperative API exposed via the optional `ref` prop. Used by the external
 * "Add discount" affordance rendered below the top mount in
 * pages/unauthenticated to expand the cart recap and drop focus straight into
 * the promo-code input — saving the user the chevron tap + the manual scroll.
 * Only the top mount is wired (the bottom mount near the pay button intentionally
 * keeps its own independent open state).
 */
export interface MobileCartSummaryHandle {
  expandAndFocusDiscount: () => void;
}

interface MobileCartSummaryProps {
  ref?: Ref<MobileCartSummaryHandle>;
  /**
   * Fired whenever the recap expands or collapses. The page uses this to hide
   * the external "Add discount" pill while the recap is open — once expanded,
   * the in-card promo input is visible, so the pill (whose only job is to open
   * the recap and focus that input) is redundant.
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * 'hero' opts this mount into a single-product showcase: when the cart holds
   * exactly one line item, it renders a large centered product image with the
   * title, price, and details below instead of the collapsible recap. Used only
   * by the top mount; the bottom mount stays 'standard'.
   *
   * 'sidebar' renders the same content as the standard recap — line items,
   * discount input, and totals — but permanently expanded (no collapse-on-tap,
   * no chevron) and never in hero mode. Used by the desktop right-column cart;
   * the Layout places it inside a sticky container (this component only owns
   * the card + content).
   */
  variant?: 'standard' | 'hero' | 'sidebar';
  /**
   * Renders ONLY the floating cart-detail sheet (scrim + drag sheet), with no
   * inline summary and no reopen handle. Used on authenticated mobile where the
   * cart is reachable solely via the header "Cart" button and the sticky-bar
   * (i) — both open this sheet through the shared atom.
   */
  sheetOnly?: boolean;
}

export const MobileCartSummary = ({
  ref,
  onOpenChange,
  variant = 'standard',
  sheetOnly = false,
}: MobileCartSummaryProps = {}) => {
  const { t } = useTranslation('common');
  const { data: cart } = useCart();
  const { data: shop } = useShop();
  const hasQuantitySelector = Boolean(shop?.features?.includes(FeatureFlag.QUANTITY_SELECTOR));
  const changedFields = useCartFieldDiff();
  const { estimatedShipping } = useEstimatedShippingAtom();
  const { themeName } = useThemeContext();
  const colors = useMemo(() => getThemeColors(themeName), [themeName]);
  const [isOpen, setIsOpen] = useState(false);
  // Ref to the promo-code <input>. Wired only when the cart is expanded and
  // resolved (the input lives inside the `isOpen` motion.div). The imperative
  // handle below uses requestAnimationFrame to defer the focus call until
  // after the open state has flushed and the input has mounted.
  const discountInputRef = useRef<HTMLInputElement | null>(null);

  // Opens the recap drawer and drops focus into the promo-code input. Shared by
  // the imperative handle (external pill, multi-product carts) and the hero's
  // own "Add promo" button. setIsOpen schedules a re-render; the input mounts on
  // the next commit, and rAF fires after that commit (before paint) by which
  // point discountInputRef is wired. Focus is idempotent if already open.
  const expandAndFocusDiscount = useCallback(() => {
    setIsOpen(true);
    requestAnimationFrame(() => {
      discountInputRef.current?.focus();
    });
  }, []);

  // Single-product hero: the "Détails du panier" drawer open state is shared via
  // an atom so the sticky Header toggle can drive it. The hero's own "Add promo"
  // button opens the drawer and focuses the discount input the same way.
  const [heroDetailsOpen, setHeroDetailsOpen] = useAtom(cartDetailsDrawerOpenAtom);
  // Drag-to-dismiss for the cart-detail sheet: the top bar is a drag handle that
  // starts a drag on the sheet; flick/drag it away far enough and it closes.
  const cartDetailDragControls = useDragControls();
  const SWIPE_DISMISS_PX = 160;
  // After the sheet is swiped off to the right, a left-pointing chevron stays on
  // the right edge as a "pull it back" handle — shown only while the cart is
  // closed, cleared whenever it reopens.
  const [reopenHandleVisible, setReopenHandleVisible] = useState(false);
  useEffect(() => {
    if (heroDetailsOpen) setReopenHandleVisible(false);
  }, [heroDetailsOpen]);
  const openHeroDetailsDiscount = useCallback(() => {
    setHeroDetailsOpen(true);
    requestAnimationFrame(() => {
      discountInputRef.current?.focus();
    });
  }, [setHeroDetailsOpen]);

  // Mobile-native discount input (replaces the heavier Leaf input which is
  // suppressed on mobile by CartDetail via isDiscountHidden).
  const { addDiscount, error: discountError, status: discountStatus } = useAddDiscount();
  const { track } = useTrackAnalytics();
  const [discountCode, setDiscountCode] = useState('');

  // Desktop sidebar: the promo field starts collapsed as a small "Add promotion
  // code" button and smoothly expands into the input on click (Stripe-style).
  // Mobile always shows the input inline.
  const [discountExpanded, setDiscountExpanded] = useState(false);
  // Full (expanded) width, measured from the section wrapper at expand time. We
  // animate the box's real width (a reflow, not a `layout` scale transform) so
  // the left-anchored icon + label never distort or shift — only the box's right
  // edge extends and the Apply button is revealed.
  const discountWrapRef = useRef<HTMLDivElement | null>(null);
  const [discountFullWidth, setDiscountFullWidth] = useState<number | undefined>(undefined);
  const expandDiscount = useCallback(() => {
    const w = discountWrapRef.current?.offsetWidth;
    if (w) setDiscountFullWidth(w);
    setDiscountExpanded(true);
    requestAnimationFrame(() => discountInputRef.current?.focus());
  }, []);

  // Quantity bottom-sheet (single-product hero). `quantityDraft` is the local,
  // uncommitted value the stepper edits; "Update" commits it via the cart
  // mutation. Synced to the live quantity each time the sheet opens.
  const updateLineItemsMutation = useUpdateLineItems();
  const [qtyDrawerOpen, setQtyDrawerOpen] = useState(false);
  const [quantityDraft, setQuantityDraft] = useState(1);
  // Lock the background checkout scroll while either hero sheet is open.
  useLockBackgroundScroll(qtyDrawerOpen);
  useLockBackgroundScroll(heroDetailsOpen);

  const onApplyDiscount = useCallback(async () => {
    const code = discountCode.trim();
    if (!code) return;
    track(AnalyticsEventName.DISCOUNT_CODE_SUBMITTED, { code });
    try {
      await addDiscount({ code });
    } catch {
      // Errors surface via discountStatus === 'error' below; no rethrow needed.
    }
  }, [addDiscount, discountCode, track]);

  // Clear the input on a successful apply.
  useEffect(() => {
    if (discountStatus === 'success' && discountCode.length > 0) {
      setDiscountCode('');
    }
  }, [discountStatus, discountCode.length]);

  const currency = cart?.currency ?? 'EUR';
  const firstItem = cart?.lineItems?.[0];
  const totalItemCount =
    cart?.lineItems?.reduce((acc, item) => acc + item.quantity, 0) ?? 0;

  const shippingLabel = useMemo(() => {
    if (cart?.shipping?.selected && cart?.shipping?.address) {
      return cart?.totalShipping === 0
        ? t('notAuthed.freeShipping')
        : t('notAuthed.shipping', { amount: formatPrice(cart.totalShipping, { currency }) });
    }
    if (estimatedShipping) {
      return estimatedShipping.amount === 0
        ? t('notAuthed.freeShipping')
        : t('notAuthed.estimatedShipping', {
            amount: formatPrice(estimatedShipping.amount, { currency }),
          });
    }
    return null;
  }, [cart, estimatedShipping, currency, t]);

  // Stock indicator ("X restant") removed from the header — it conflates a
  // scarcity signal with shipping metadata. Leaf renders stock at the
  // line-item level (leftQuantityTitle) when it matters.
  const summaryLine = shippingLabel ?? '';
  // Header primary label is just the item count ("N articles"). The "Votre panier ·"
  // prefix was dropped — even with short labels it pushed the count off-screen on
  // the smallest phones, where the right side of the row also holds the total and
  // the chevron. Empty-cart state falls back to the static label.
  const countLabel = t(
    totalItemCount > 1 ? 'commons.cart_articles_plurials' : 'commons.cart_articles',
    { count: totalItemCount },
  );
  const headerPrimaryLabel =
    totalItemCount > 0 ? countLabel : t('commons.cart_summary_label');
  const secondItem = cart?.lineItems?.[1];
  const totalPrice = formatPrice(cart?.totalAmount ?? 0, { currency });

  // Skeleton state machine. The card frame (the outer Box below) is rendered
  // unconditionally so layout is stable from t=0 — no shift when the cart
  // resolves. Inside, we cross-fade between a quiet silhouette and the real
  // content. The silhouette only appears if the cart hasn't resolved within
  // SKELETON_DELAY_MS (otherwise it would flash on cache hits), and only on
  // the initial mount — subsequent cart updates animate in place via the
  // existing CartDetail primitives, not via this swap.
  const cartReady = Boolean(cart && firstItem);
  const hasResolvedRef = useRef(false);
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (cartReady) {
      hasResolvedRef.current = true;
      setShowSkeleton(false);
      return;
    }
    if (hasResolvedRef.current) return;
    const timer = setTimeout(() => setShowSkeleton(true), SKELETON_DELAY_MS);
    return () => clearTimeout(timer);
  }, [cartReady]);

  // Single-product hero (top mount only). Shows a large centered product image
  // with the title, price, and details below — a showcase, not the collapsible
  // recap (which stays on the bottom mount). Gated on exactly one line item.
  const isHero = variant === 'hero' && (cart?.lineItems?.length ?? 0) === 1 && Boolean(firstItem);
  // Top mount on mobile with a non-empty cart. Single item → showcase hero;
  // multiple items → compact resting row. Both drive the same header-anchored
  // floating details sheet via the shared atom (PROTOTYPE: extends the
  // always-reachable recap to multi-product carts, not just single).
  const isHeroMount = variant === 'hero' && Boolean(firstItem);
  const isMultiHero = isHeroMount && !isHero;

  // Desktop right-column sidebar: the standard recap body, permanently
  // expanded. `isHero`/`isHeroMount` already gate on variant === 'hero', so the
  // sidebar never takes the hero path. We force the recap open by OR-ing
  // `isSidebar` into the open boolean and drop the chevron/tap-to-collapse
  // affordance below.
  const isSidebar = variant === 'sidebar';
  const recapOpen = isOpen || isSidebar;

  // Desktop sidebar single-product showcase: when the sidebar holds exactly one
  // line item, mirror the mobile hero treatment (big centered image + name +
  // price + compact stepper/remove) above the totals, and suppress Leaf's
  // duplicate line-item list (via hideProducts on CartDetail below).
  // Desktop sidebar renders every cart through the same compact list row,
  // regardless of item count — a single product is a normal list row, not a
  // centered showcase (a phone pattern that doesn't fit the desktop summary
  // column). Mobile's single-product hero (variant 'hero') is unaffected.
  const isSingleProductSidebar = false;

  // Direct quantity setter for the sidebar stepper / remove control. Reuses the
  // same updateLineItems mutation the hero quantity sheet commits through; a
  // quantity of 0 removes the line. No local draft — each tap commits.
  const setSidebarQuantity = useCallback(
    async (newQuantity: number) => {
      if (!firstItem) return;
      try {
        await updateLineItemsMutation.mutateAsync({
          lineItems: [
            {
              id: firstItem.id,
              productId: firstItem.productId,
              variantId: firstItem.variantId,
              quantity: newQuantity,
              customizationId: firstItem.customizationId,
            },
          ],
        });
      } catch {
        // Error toast is surfaced inside the mutation's onError.
      }
    },
    [firstItem, updateLineItemsMutation],
  );

  // Hero mounts open the floating sheet (shared atom); the external discount
  // pill and onOpenChange therefore target that drawer rather than the inline
  // `isOpen` used by the standard card.
  useImperativeHandle(
    ref,
    () => ({ expandAndFocusDiscount: isHeroMount ? openHeroDetailsDiscount : expandAndFocusDiscount }),
    [isHeroMount, openHeroDetailsDiscount, expandAndFocusDiscount],
  );

  // Only the inline standard card's open state drives the external "Add
  // discount" pill. The hero floating drawer intentionally does NOT hide the
  // pill — collapsing it while the drawer is open reflows the (dimmed but
  // visible) form under the scrim.
  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);
  const heroVariants = (firstItem?.variants ?? []).filter(
    (value) => value && value.toLowerCase() !== 'default title',
  );
  const heroVariantLine = heroVariants.join(' · ');
  const normalize = (s: string) => s.replace(/[\s/·,-]/g, '').toLowerCase();
  const heroDescription =
    firstItem?.description &&
    firstItem.description.toLowerCase() !== 'default title' &&
    normalize(firstItem.description) !== normalize(heroVariantLine)
      ? firstItem.description
      : undefined;
  const heroQuantity = firstItem?.quantity ?? 1;
  const heroHasOriginal = Boolean(
    firstItem?.originalAmount &&
      firstItem.originalAmount !== 0 &&
      firstItem.originalAmount !== firstItem.unitAmount,
  );
  const heroLineTotal = firstItem?.totalAmount ?? 0;
  const heroDisplayedTotal = heroHasOriginal
    ? (firstItem?.originalAmount ?? 0) * heroQuantity
    : heroLineTotal;
  const heroDiscountedTotal = firstItem?.discountAmount
    ? heroLineTotal - Math.abs(firstItem.discountAmount)
    : heroLineTotal;
  const heroShouldStroke = heroDiscountedTotal !== heroLineTotal || heroHasOriginal;

  // Upper bound for the quantity stepper, when the line item exposes stock.
  const maxQuantity =
    typeof firstItem?.quantityAvailable === 'number' && firstItem.quantityAvailable > 0
      ? firstItem.quantityAvailable
      : undefined;

  const onUpdateQuantity = useCallback(async () => {
    if (!firstItem) return;
    try {
      await updateLineItemsMutation.mutateAsync({
        lineItems: [
          {
            id: firstItem.id,
            productId: firstItem.productId,
            variantId: firstItem.variantId,
            quantity: quantityDraft,
            customizationId: firstItem.customizationId,
          },
        ],
      });
      setQtyDrawerOpen(false);
    } catch {
      // Error toast is surfaced inside the mutation's onError.
    }
  }, [firstItem, quantityDraft, updateLineItemsMutation]);

  // Album-stack thumbnail: single tile for 1 item, two offset+rotated tiles for
  // 2+ (iOS-Photos style). Shared by the standard inline card and the
  // multi-product hero resting row.
  const thumbnail = secondItem ? (
    <Box flexShrink={0} position="relative" w="3.75rem" h="3.75rem">
      <Box
        position="absolute"
        top="0"
        left="0.875rem"
        w="2.875rem"
        h="2.875rem"
        borderRadius="0.5rem"
        overflow="hidden"
        bg={colors.bgSecondary}
        border="1.5px solid white"
        transform="rotate(-6deg)"
        transformOrigin="center"
        zIndex={0}
        boxShadow="0 1px 1px rgba(0, 0, 0, 0.05)"
      >
        {secondItem.imageUrl ? (
          <Image src={secondItem.imageUrl} alt="" w="full" h="full" objectFit="cover" />
        ) : null}
      </Box>
      <Box
        position="absolute"
        top="0.875rem"
        left="0"
        w="2.875rem"
        h="2.875rem"
        borderRadius="0.5rem"
        overflow="hidden"
        bg={colors.bgSecondary}
        border="1.5px solid white"
        zIndex={1}
        boxShadow="0 1px 1px rgba(0, 0, 0, 0.05)"
      >
        {firstItem?.imageUrl ? (
          <Image src={firstItem.imageUrl} alt={firstItem.name} w="full" h="full" objectFit="cover" />
        ) : null}
      </Box>
    </Box>
  ) : (
    <Box flexShrink={0} w="3.25rem" h="3.25rem" borderRadius="0.5rem" overflow="hidden" bg={colors.bgSecondary}>
      {firstItem?.imageUrl ? (
        <Image src={firstItem.imageUrl} alt={firstItem.name} w="full" h="full" objectFit="cover" />
      ) : null}
    </Box>
  );

  // Desktop sidebar: start collapsed as a compact "Add promotion code" chip.
  const showDiscountButton = isSidebar && !discountExpanded;

  // The actual input row (tag icon + naked input + Apply). Shared by the mobile
  // box and the desktop expanded state.
  const discountInputRow = (
    <HStack
      px={isSidebar ? 5 : 4}
      h={isSidebar ? 'full' : undefined}
      minH={isSidebar ? undefined : '52px'}
      gap={3}
      align="center"
      w="full"
    >
      {/* No leading icon on desktop (matches the design); mobile keeps it. */}
      {!isSidebar && (
        <Box flexShrink={0} color="var(--text-muted, #8F8F8F)">
          <TagIcon size={20} />
        </Box>
      )}
      <input
        ref={discountInputRef}
        className={NAKED_INPUT_CLASS}
        style={NAKED_INPUT_STYLE}
        placeholder={isSidebar ? t('commons.add_promotion_code') : t('commons.discount_code')}
        value={discountCode}
        onChange={(e) => setDiscountCode(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void onApplyDiscount();
          }
        }}
        // Desktop: clicking away from an empty field collapses it back to the
        // "Add promotion code" chip (the width/height animation reverses).
        onBlur={() => {
          if (isSidebar && discountCode.trim().length === 0) {
            setDiscountExpanded(false);
          }
        }}
        aria-label={t('commons.discount_code')}
        autoComplete="off"
      />
      {discountStatus === 'pending' ? (
        <Spinner size="sm" color="var(--text-muted, #8F8F8F)" flexShrink={0} />
      ) : discountCode.trim().length > 0 || !isSidebar ? (
        // Desktop: Apply only appears once a code is typed (empty field reads as
        // a clean placeholder, per the design). Mobile keeps it always visible.
        <Text
          as="button"
          type="button"
          onClick={() => {
            void onApplyDiscount();
          }}
          fontSize="md"
          fontWeight={discountCode.trim().length > 0 ? 600 : 400}
          color={
            discountCode.trim().length > 0 ? 'var(--text, #1C1C1C)' : 'var(--text-muted, #8F8F8F)'
          }
          bg="transparent"
          flexShrink={0}
          aria-disabled={discountCode.trim().length === 0}
          pointerEvents={discountCode.trim().length === 0 ? 'none' : 'auto'}
          data-ph-capture-attribute-tracking-id="mobile-discount-apply"
        >
          {t('buttons.add')}
        </Text>
      ) : null}
    </HStack>
  );

  // Shared promo-code section. Desktop sidebar collapses to a chip that smoothly
  // widens into the input on click (shared bg/border/radius → the chip morphs
  // into the field). Mobile always shows the input inline.
  const discountSection = (
    <Box ref={discountWrapRef} w="full">
      {isSidebar ? (
        <motion.div
          // Animate real width (reflow), not `layout` (scale transform) — the
          // latter distorts/shifts the icon + text during the morph.
          initial={false}
          animate={{
            width: showDiscountButton ? 'auto' : (discountFullWidth ?? '100%'),
            // Same height in both states so the input keeps the button's height —
            // only the width animates, no vertical shift.
            height: 44,
            // Collapsed: grey fill, no border (a chip). Expanded: white fill with
            // a light border (an input). Both animate so the chip morphs.
            backgroundColor: showDiscountButton ? '#EFEFEF' : '#FFFFFF',
            borderColor: showDiscountButton ? 'rgba(0, 0, 0, 0)' : '#E0E0E0',
          }}
          transition={{ duration: 0.3, ease: IOS_CURVE }}
          onClick={showDiscountButton ? expandDiscount : undefined}
          role={showDiscountButton ? 'button' : undefined}
          tabIndex={showDiscountButton ? 0 : undefined}
          onKeyDown={
            showDiscountButton
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    expandDiscount();
                  }
                }
              : undefined
          }
          style={{
            // inline-flex so width:auto hugs the content (chip) when collapsed;
            // the expand animates width out to the measured full width.
            display: 'inline-flex',
            verticalAlign: 'top',
            boxSizing: 'border-box',
            border: '1px solid transparent',
            borderRadius: '0.75rem',
            overflow: 'hidden',
            cursor: showDiscountButton ? 'pointer' : 'auto',
          }}
        >
          {showDiscountButton ? (
            <HStack px={5} h="full" gap={3} align="center" w="fit-content">
              <Text fontSize="md" fontWeight={400} color="var(--text, #1C1C1C)" whiteSpace="nowrap">
                {t('commons.add_promotion_code')}
              </Text>
            </HStack>
          ) : (
            discountInputRow
          )}
        </motion.div>
      ) : (
        <Box bg="white" borderRadius="0.75rem" border="1px solid var(--border, #E0E0E0)" overflow="hidden">
          {discountInputRow}
        </Box>
      )}

      {discountStatus === 'error' && (
        <Text fontSize="sm" fontWeight={400} color="var(--danger, #EE5829)" mt={2} px={4}>
          {discountError?.message ?? t('errors.commons.unknown_discount', { code: discountCode })}
        </Text>
      )}
    </Box>
  );

  if (isHeroMount && firstItem) {
    return (
      <>
        {/* Scrim — dims the rest of the form (10% black) to focus on the cart
            details while the drawer is open. Tapping it closes the drawer. */}
        <AnimatePresence>
          {heroDetailsOpen && (
            <motion.div
              key="mobile-cart-hero-scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: IOS_CURVE }}
              onClick={() => setHeroDetailsOpen(false)}
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.1)',
                zIndex: 1,
              }}
            />
          )}
        </AnimatePresence>
        {/* Inline cart display — single-product showcase or multi-product
            resting row. Skipped entirely in sheetOnly mode (authenticated):
            the cart is reached only via the header "Cart" button + sticky-bar
            (i), both of which open the floating sheet below. */}
        {!sheetOnly && (isMultiHero ? (
          <Box w="full" overflow="hidden" borderRadius="0.75rem" bg={colors.cardBg} border={`1px solid ${colors.border}`}>
            <Flex
              as="button"
              type="button"
              onClick={() => setHeroDetailsOpen((v) => !v)}
              align="center"
              gap={3}
              px={4}
              py={3}
              w="full"
              textAlign="left"
              bg="transparent"
              aria-expanded={heroDetailsOpen}
              aria-controls="mobile-cart-hero-details"
            >
              {thumbnail}
              <VStack flex="1" align="stretch" spacing={0.5} minW={0}>
                <Text color={colors.text} fontWeight={600} fontSize="1rem" letterSpacing="-0.01em" noOfLines={1}>
                  {headerPrimaryLabel}
                </Text>
                {summaryLine ? (
                  <Text color={colors.textMuted} fontSize="0.875rem" noOfLines={1}>
                    {summaryLine}
                  </Text>
                ) : null}
              </VStack>
              <HStack spacing={3} flexShrink={0} align="center">
                <Box
                  opacity={heroDetailsOpen ? 0 : 1}
                  transition="opacity 350ms cubic-bezier(0.32, 0.72, 0, 1)"
                  aria-hidden={heroDetailsOpen}
                >
                  <FieldDiff
                    fieldKey="totalAmount"
                    changedFields={changedFields}
                    color={colors.text}
                    fontWeight={600}
                    fontSize="1rem"
                    skeletonWidth="3rem"
                  >
                    {totalPrice}
                  </FieldDiff>
                </Box>
                <Chevron color={colors.textMuted} isOpen={heroDetailsOpen} />
              </HStack>
            </Flex>
          </Box>
        ) : (
        <Box w="full" overflow="hidden" borderRadius="0.75rem" bg={colors.cardBg}>
        {/* The "Détails du panier" toggle now lives in the sticky Header
            (top-right); it drives heroDetailsOpen via the shared atom. */}
        <VStack spacing={3} align="center" px={4} pt={5} pb={5} w="full">
          <Box
            w="full"
            maxW="10rem"
            bg={colors.bgSecondary}
            borderRadius="0.75rem"
            overflow="hidden"
            sx={{ aspectRatio: '1 / 1' }}
          >
            {firstItem.imageUrl ? (
              <Image
                src={firstItem.imageUrl}
                alt={firstItem.name}
                w="full"
                h="full"
                objectFit="cover"
              />
            ) : null}
          </Box>
          <VStack spacing={1} align="center" w="full">
            <Text
              color={colors.text}
              fontWeight={600}
              fontSize="1.0625rem"
              letterSpacing="-0.01em"
              textAlign="center"
              noOfLines={2}
            >
              {firstItem.name}
            </Text>
            {heroShouldStroke ? (
              <HStack spacing={2} align="baseline" justify="center">
                <Text
                  color={colors.textMuted}
                  fontSize="0.875rem"
                  textDecorationLine="line-through"
                >
                  {formatPrice(heroDisplayedTotal, { currency })}
                </Text>
                <Text
                  color="var(--savings, #2E7D52)"
                  fontWeight={600}
                  fontSize="1.25rem"
                  letterSpacing="-0.02em"
                >
                  {formatPrice(heroDiscountedTotal, { currency })}
                </Text>
              </HStack>
            ) : (
              <Text
                color={colors.text}
                fontWeight={600}
                fontSize="1.25rem"
                letterSpacing="-0.02em"
                textAlign="center"
              >
                {formatPrice(heroLineTotal, { currency })}
              </Text>
            )}
            {heroVariantLine ? (
              <Text color={colors.textSecondary} fontSize="0.875rem" textAlign="center">
                {heroVariantLine}
              </Text>
            ) : null}
            {heroDescription ? (
              <Text color={colors.textMuted} fontSize="0.875rem" textAlign="center">
                {heroDescription}
              </Text>
            ) : null}
            {/* Per-unit breakdown — only meaningful when more than one unit. */}
            {heroQuantity > 1 ? (
              <Text color={colors.textMuted} fontSize="0.875rem" textAlign="center">
                {t('commons.quantity_each', {
                  count: heroQuantity,
                  price: formatPrice(firstItem.unitAmount, { currency }),
                })}
              </Text>
            ) : null}
          </VStack>

          {/* Chip row: "Add discount" (opens the drawer + focuses the promo
              input) and "Qty N" (opens the same cart-details drawer, where the
              quantity stepper lives). Mirrors the paired dropdown chips in the
              redesign mockup. */}
          <HStack spacing={2} justify="center" flexWrap="wrap">
            <HStack
              as="button"
              type="button"
              onClick={openHeroDetailsDiscount}
              spacing={1.5}
              bg={colors.bgSecondary}
              borderRadius="full"
              px={3}
              py={1.5}
              color={colors.text}
              transition="opacity 150ms ease"
              _active={{ opacity: 0.7 }}
              data-ph-capture-attribute-tracking-id="mobile-hero-add-discount"
            >
              <Box flexShrink={0} color={colors.textMuted}>
                <TagIcon />
              </Box>
              <Text fontSize="0.875rem" fontWeight={500} lineHeight={1}>
                {t('commons.add_discount_label')}
              </Text>
            </HStack>

            {/* The quantity chip + its bottom sheet are gated on the shop's
                quantity_selector feature — when off, customers can't change
                quantities, matching the in-cart selector's own gating. */}
            {hasQuantitySelector && (
              <HStack
                as="button"
                type="button"
                onClick={() => {
                  setQuantityDraft(heroQuantity);
                  setQtyDrawerOpen(true);
                }}
                spacing={1.5}
                bg={colors.bgSecondary}
                borderRadius="full"
                px={3}
                py={1.5}
                color={colors.text}
                transition="opacity 150ms ease"
                _active={{ opacity: 0.7 }}
                aria-haspopup="dialog"
                aria-expanded={qtyDrawerOpen}
                data-ph-capture-attribute-tracking-id="mobile-hero-quantity"
              >
                <Text fontSize="0.875rem" fontWeight={500} lineHeight={1}>
                  {t('commons.quantity')} {heroQuantity}
                </Text>
                <Box flexShrink={0}>
                  <Chevron color={colors.textMuted} isOpen={qtyDrawerOpen} />
                </Box>
              </HStack>
            )}
          </HStack>
        </VStack>

        </Box>
        ))}

        {/* Details sheet — fixed, floats just below the sticky header on top of
            the dimmed form, so the whole recap stays visible regardless of how
            far the form has been scrolled. Sits above the scrim (zIndex 1) but
            below the sticky header (zIndex 1000) so its "Fermer" toggle stays
            tappable. */}
        <AnimatePresence initial={false}>
          {heroDetailsOpen && (
            <motion.div
              key="mobile-cart-hero-details"
              id="mobile-cart-hero-details"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{
                opacity: 0,
                y: -40,
                scale: 0.96,
                // Explicit, slightly longer ease so the close glides (retracts up
                // into the header) from wherever the drag left it — not an instant
                // pop. Overrides the drag transform on the way out.
                transition: { duration: 0.32, ease: IOS_CURVE },
              }}
              transition={{ duration: 0.3, ease: IOS_CURVE }}
              drag
              dragControls={cartDetailDragControls}
              dragListener={false}
              dragConstraints={{ top: 0, bottom: 0, left: 0, right: 0 }}
              dragElastic={0.6}
              onDragEnd={(_, info) => {
                // Free drag in any direction, springs back to place. Dismiss on a
                // clear right-swipe, an up-flick, or a strong drag away.
                const distance = Math.hypot(info.offset.x, info.offset.y);
                const dismissedRight = info.offset.x > SWIPE_DISMISS_PX || info.velocity.x > 600;
                const dismiss =
                  dismissedRight ||
                  info.offset.y < -80 ||
                  info.velocity.y < -500 ||
                  distance > SWIPE_DISMISS_PX;
                if (dismiss) {
                  setHeroDetailsOpen(false);
                  // Only a rightward swipe leaves the "pull back" edge handle.
                  if (dismissedRight) setReopenHandleVisible(true);
                }
              }}
              style={{
                position: 'fixed',
                top: 'calc(var(--checkout-header-height, 4rem) + 8px)',
                left: '0.5rem',
                right: '0.5rem',
                zIndex: 900,
                maxHeight: 'calc(100dvh - var(--checkout-header-height, 4rem) - 24px)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                background: colors.cardBg,
                borderRadius: '0.75rem',
                boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
              }}
            >
              {/* Top bar = drag handle. Grabber pill centered, circular "×" at the
                  top-right. Pressing here starts a y-drag on the sheet (the
                  scroll area below keeps its own touch). */}
              <Box
                flexShrink={0}
                position="relative"
                px={3}
                pt={2.5}
                pb={1}
                onPointerDown={(e) => cartDetailDragControls.start(e)}
                sx={{ touchAction: 'none' }}
                cursor="grab"
                _active={{ cursor: 'grabbing' }}
              >
                <Box mx="auto" w="2.25rem" h="0.25rem" borderRadius="full" bg={colors.border} />
                <Flex
                  as="button"
                  type="button"
                  onClick={() => setHeroDetailsOpen(false)}
                  onPointerDown={(e) => e.stopPropagation()}
                  position="absolute"
                  top={1.5}
                  right={3}
                  w="2rem"
                  h="2rem"
                  align="center"
                  justify="center"
                  borderRadius="full"
                  bg={colors.bgSecondary}
                  color={colors.textMuted}
                  _active={{ opacity: 0.7 }}
                  aria-label={t('buttons.close')}
                >
                  <CloseIcon />
                </Flex>
              </Box>
              <Box flex="1" minH={0} overflowY="auto" px={4} pt={1} pb={4}>
                {/* Single-product hero edits quantity via the dedicated bottom
                    sheet, so the in-cart selector is hidden here to avoid a
                    duplicate control. Multi-product keeps its selector. */}
                <CartDetail
                  extraBeforeDiscounts={discountSection}
                  isQuantityHidden={isHero && hasQuantitySelector && !sheetOnly}
                />
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Right-edge "pull it back" handle — a left-pointing chevron that appears
            only once the sheet has been swiped off to the right and stays while
            the cart is closed. Tap it to bring the cart detail back. */}
        <AnimatePresence>
          {!sheetOnly && reopenHandleVisible && !heroDetailsOpen && (
            <motion.div
              key="cart-reopen-handle"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={{ duration: 0.25, ease: IOS_CURVE }}
              style={{ position: 'fixed', top: '50%', right: 0, y: '-50%', zIndex: 950 }}
            >
              <Box
                as="button"
                type="button"
                onClick={() => setHeroDetailsOpen(true)}
                display="flex"
                alignItems="center"
                justifyContent="center"
                w="1.75rem"
                h="3rem"
                pr={0.5}
                bg="rgba(0, 0, 0, 0.55)"
                color="white"
                borderTopLeftRadius="full"
                borderBottomLeftRadius="full"
                boxShadow="-2px 0 10px rgba(0, 0, 0, 0.18)"
                aria-label={t('commons.cart')}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path
                    d="M15 6l-6 6 6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quantity editor — a bottom sheet that slides up over a dimmed form.
            Edits the single line item's quantity locally, commits on "Update". */}
        <AnimatePresence>
          {qtyDrawerOpen && (
            <motion.div
              key="mobile-qty-scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25, ease: IOS_CURVE }}
              onClick={() => setQtyDrawerOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.4)', zIndex: 2000 }}
            />
          )}
        </AnimatePresence>
        <AnimatePresence>
          {qtyDrawerOpen && (
            <motion.div
              key="mobile-qty-sheet"
              role="dialog"
              aria-modal="true"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ duration: 0.35, ease: IOS_CURVE }}
              style={{
                position: 'fixed',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2001,
                background: colors.cardBg,
                borderTopLeftRadius: '1.25rem',
                borderTopRightRadius: '1.25rem',
                boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.18)',
              }}
            >
              <Box px={4} pt={4} pb="calc(1rem + env(safe-area-inset-bottom))">
                {/* Header: thumbnail + "Update quantity" + product name + close */}
                <HStack spacing={3} align="center" mb={6}>
                  <Box
                    flexShrink={0}
                    w="2.75rem"
                    h="2.75rem"
                    borderRadius="0.5rem"
                    overflow="hidden"
                    bg={colors.bgSecondary}
                  >
                    {firstItem.imageUrl ? (
                      <Image src={firstItem.imageUrl} alt={firstItem.name} w="full" h="full" objectFit="cover" />
                    ) : null}
                  </Box>
                  <VStack flex="1" align="start" spacing={0} minW={0}>
                    <Text color={colors.text} fontWeight={600} fontSize="1rem" noOfLines={1}>
                      {t('commons.update_quantity')}
                    </Text>
                    <Text color={colors.textMuted} fontSize="0.875rem" noOfLines={1}>
                      {firstItem.name}
                    </Text>
                  </VStack>
                  <Box
                    as="button"
                    type="button"
                    onClick={() => setQtyDrawerOpen(false)}
                    flexShrink={0}
                    color={colors.textMuted}
                    p={1}
                    aria-label={t('buttons.close')}
                  >
                    <CloseIcon />
                  </Box>
                </HStack>

                {/* Stepper: − [value] + */}
                <HStack justify="center" spacing={5} mb={6}>
                  <Flex
                    as="button"
                    type="button"
                    onClick={() => setQuantityDraft((q) => Math.max(1, q - 1))}
                    disabled={quantityDraft <= 1}
                    w="2.75rem"
                    h="2.75rem"
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg={colors.bgSecondary}
                    color={colors.text}
                    opacity={quantityDraft <= 1 ? 0.4 : 1}
                    transition="opacity 150ms ease"
                    aria-label="Decrease quantity"
                  >
                    <MinusIcon />
                  </Flex>
                  <Flex
                    align="center"
                    justify="center"
                    minW="4rem"
                    h="2.75rem"
                    px={3}
                    borderRadius="0.625rem"
                    border={`1px solid ${colors.border}`}
                    color={colors.text}
                    fontWeight={600}
                    fontSize="1.125rem"
                  >
                    {quantityDraft}
                  </Flex>
                  <Flex
                    as="button"
                    type="button"
                    onClick={() =>
                      setQuantityDraft((q) => (maxQuantity ? Math.min(maxQuantity, q + 1) : q + 1))
                    }
                    disabled={maxQuantity ? quantityDraft >= maxQuantity : false}
                    w="2.75rem"
                    h="2.75rem"
                    align="center"
                    justify="center"
                    borderRadius="full"
                    bg={colors.bgSecondary}
                    color={colors.text}
                    opacity={maxQuantity && quantityDraft >= maxQuantity ? 0.4 : 1}
                    transition="opacity 150ms ease"
                    aria-label="Increase quantity"
                  >
                    <PlusIcon />
                  </Flex>
                </HStack>

                {/* Commit */}
                <Flex
                  as="button"
                  type="button"
                  onClick={onUpdateQuantity}
                  disabled={updateLineItemsMutation.isPending}
                  w="full"
                  h="3.25rem"
                  align="center"
                  justify="center"
                  borderRadius="0.75rem"
                  bg="var(--pay-button-bg-enabled, #1C1C1C)"
                  color="var(--pay-button-text-enabled, #FFFFFF)"
                  fontWeight={600}
                  fontSize="1rem"
                  opacity={updateLineItemsMutation.isPending ? 0.7 : 1}
                  transition="opacity 150ms ease"
                >
                  {updateLineItemsMutation.isPending ? <Spinner size="sm" /> : t('buttons.update')}
                </Flex>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <Box
      w="full"
      bg={colors.cardBg}
      borderRadius="0.75rem"
      border={isSidebar ? undefined : `1px solid ${colors.border}`}
      overflow={isSidebar ? 'visible' : 'hidden'}
      minH={isSidebar ? undefined : HEADER_HEIGHT}
    >
      <AnimatePresence mode="wait" initial={false}>
        {cart && firstItem ? (
          <motion.div
            key="cart-summary-real"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: IOS_CURVE }}
          >
      {!isSidebar && (
      <Flex
        as="button"
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        align="center"
        gap={3}
        px={4}
        py={3}
        w="full"
        minH={HEADER_HEIGHT}
        textAlign="left"
        bg="transparent"
        aria-expanded={isOpen}
        aria-controls="mobile-cart-summary-content"
      >
        {/* Thumbnail: single tile when 1 line item, two offset+rotated tiles
            when 2+ — iOS-Photos-album style. Geometry inside the 60 px (3.75
            rem) container: each tile is 46 px (2.875 rem); the rear is
            top-right + rotated -6°, the front is bottom-left flat. ~14 px of
            the rear sticks out past the front so the stack reads at a glance.
            (A purely-centered stack with only an 8° rotation peeked just ~3 px
            at the corners — visually indistinguishable from a single tile.) */}
        {thumbnail}

        <VStack flex="1" align="stretch" spacing={0.5} minW={0}>
          <Text
            color={colors.text}
            fontWeight={600}
            fontSize="1rem"
            letterSpacing="-0.01em"
            noOfLines={1}
          >
            {headerPrimaryLabel}
          </Text>
          {summaryLine ? (
            <Text color={colors.textMuted} fontSize="0.875rem" noOfLines={1}>
              {summaryLine}
            </Text>
          ) : null}
        </VStack>

        <HStack spacing={3} flexShrink={0} align="center">
          <Box
            opacity={isOpen ? 0 : 1}
            transition="opacity 350ms cubic-bezier(0.32, 0.72, 0, 1)"
            aria-hidden={isOpen}
          >
            <FieldDiff
              fieldKey="totalAmount"
              changedFields={changedFields}
              color={colors.text}
              fontWeight={600}
              fontSize="1rem"
              skeletonWidth="3rem"
            >
              {totalPrice}
            </FieldDiff>
          </Box>
          {isSidebar ? null : <Chevron color={colors.textMuted} isOpen={isOpen} />}
        </HStack>
      </Flex>
      )}

      <AnimatePresence initial={false}>
        {recapOpen && (
          <motion.div
            key="mobile-cart-summary-content"
            // Sidebar is always open and static: skip the height collapse
            // animation (its measured height clips the trailing promo input)
            // and let content flow at natural height. Mobile keeps the collapse.
            initial={isSidebar ? false : { height: 0, opacity: 0 }}
            animate={isSidebar ? { opacity: 1 } : { height: 'auto', opacity: 1 }}
            exit={isSidebar ? undefined : { height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
            style={{ overflow: isSidebar ? 'visible' : 'hidden', width: '100%' }}
          >
            <Box id="mobile-cart-summary-content" px={4} pb={3}>
              {!isSidebar && (
                <Divider
                  borderBottomWidth="1px"
                  borderColor="var(--border, #E0E0E0)"
                  opacity={1}
                  mb={3}
                />
              )}
              {isSingleProductSidebar && firstItem ? (
                <VStack spacing={2.5} align="start" pt={2} pb={3} w="full">
                  <Box
                    w="full"
                    maxW="10rem"
                    bg={colors.bgSecondary}
                    borderRadius="0.75rem"
                    overflow="hidden"
                    sx={{ aspectRatio: '1 / 1' }}
                  >
                    {firstItem.imageUrl ? (
                      <Image
                        src={firstItem.imageUrl}
                        alt={firstItem.name}
                        w="full"
                        h="full"
                        objectFit="cover"
                      />
                    ) : null}
                  </Box>
                  <VStack spacing={1} align="start" w="full">
                    <Text
                      color={colors.text}
                      fontWeight={600}
                      fontSize="0.9rem"
                      letterSpacing="-0.01em"
                      noOfLines={2}
                    >
                      {firstItem.name}
                    </Text>
                    {heroShouldStroke ? (
                      <HStack spacing={2} align="baseline" justify="flex-start">
                        <Text
                          color={colors.textMuted}
                          fontSize="0.75rem"
                          textDecorationLine="line-through"
                        >
                          {formatPrice(heroDisplayedTotal, { currency })}
                        </Text>
                        <Text
                          color="var(--savings, #2E7D52)"
                          fontWeight={600}
                          fontSize="1.0625rem"
                          letterSpacing="-0.02em"
                        >
                          {formatPrice(heroDiscountedTotal, { currency })}
                        </Text>
                      </HStack>
                    ) : (
                      <Text
                        color={colors.text}
                        fontWeight={600}
                        fontSize="1.0625rem"
                        letterSpacing="-0.02em"
                      >
                        {formatPrice(heroLineTotal, { currency })}
                      </Text>
                    )}
                    {heroVariantLine ? (
                      <Text color={colors.textSecondary} fontSize="0.75rem">
                        {heroVariantLine}
                      </Text>
                    ) : null}
                    {heroQuantity > 1 ? (
                      <Text color={colors.textMuted} fontSize="0.75rem">
                        {t('commons.quantity_each', {
                          count: heroQuantity,
                          price: formatPrice(firstItem.unitAmount, { currency }),
                        })}
                      </Text>
                    ) : null}
                  </VStack>

                  {/* Compact stepper: − [qty] +. Each control commits
                      immediately through setSidebarQuantity. */}
                  <HStack spacing={3} justify="flex-start" align="center">
                    {hasQuantitySelector ? (
                      <HStack spacing={2} align="center">
                        <Flex
                          as="button"
                          type="button"
                          onClick={() => void setSidebarQuantity(Math.max(1, heroQuantity - 1))}
                          disabled={heroQuantity <= 1 || updateLineItemsMutation.isPending}
                          w="1.9rem"
                          h="1.9rem"
                          align="center"
                          justify="center"
                          borderRadius="full"
                          bg={colors.bgSecondary}
                          color={colors.text}
                          opacity={heroQuantity <= 1 || updateLineItemsMutation.isPending ? 0.4 : 1}
                          transition="opacity 150ms ease"
                          aria-label={t('commons.quantity')}
                        >
                          <MinusIcon />
                        </Flex>
                        <Flex
                          align="center"
                          justify="center"
                          minW="2.25rem"
                          h="1.9rem"
                          px={2}
                          color={colors.text}
                          fontWeight={600}
                          fontSize="0.875rem"
                        >
                          {heroQuantity}
                        </Flex>
                        <Flex
                          as="button"
                          type="button"
                          onClick={() => void setSidebarQuantity(heroQuantity + 1)}
                          disabled={
                            (maxQuantity ? heroQuantity >= maxQuantity : false) ||
                            updateLineItemsMutation.isPending
                          }
                          w="1.9rem"
                          h="1.9rem"
                          align="center"
                          justify="center"
                          borderRadius="full"
                          bg={colors.bgSecondary}
                          color={colors.text}
                          opacity={
                            (maxQuantity ? heroQuantity >= maxQuantity : false) ||
                            updateLineItemsMutation.isPending
                              ? 0.4
                              : 1
                          }
                          transition="opacity 150ms ease"
                          aria-label={t('commons.quantity')}
                        >
                          <PlusIcon />
                        </Flex>
                      </HStack>
                    ) : null}
                  </HStack>
                </VStack>
              ) : null}
              <CartDetail
                extraBeforeDiscounts={discountSection}
                hideDiscountInput={isSidebar}
                hideProducts={isSingleProductSidebar}
                dense={isSidebar}
              />
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
          </motion.div>
        ) : showSkeleton ? (
          <motion.div
            key="cart-summary-skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: IOS_CURVE }}
          >
            <HStack
              align="center"
              gap={3}
              px={4}
              py={3}
              w="full"
              minH={HEADER_HEIGHT}
              opacity={0.6}
              pointerEvents="none"
              aria-hidden
            >
              <Box
                flexShrink={0}
                w="3.25rem"
                h="3.25rem"
                borderRadius="0.5rem"
                bg={colors.bgSecondary}
              />
              <Box flex="1" minW={0}>
                <Box w="60%" h="0.875rem" borderRadius="0.25rem" bg={colors.border} />
              </Box>
              <Box flexShrink={0} w="3rem" h="0.875rem" borderRadius="0.25rem" bg={colors.border} />
            </HStack>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Box>
  );
};
